// Wizard Nieuwe reservering: wat meerdere stappen delen — de klant, de periode en de knoppen onderaan.
import type { ReactNode } from "react";
import { useCustom, useTranslate } from "@refinedev/core";
import { ChevronLeft, ChevronRight, Home, Mail, Phone } from "lucide-react";

import type { PensionKlantData } from "../pensionKlantTypes";
import { datumMetJaar } from "../pensionklant/hulp";

/** De klant waarvoor de reservering wordt gemaakt, met wat de wizard ervan laat zien. */
export interface GekozenKlant {
  id: number;
  naam: string;
  adres: string | null;
  email: string | null;
  telefoon: string | null;
  dieren?: number;
  pensionBoekingen?: number;
}

export interface Periode {
  aankomst: string;
  aankomstTijd: string;
  vertrek: string;
  vertrekTijd: string;
}

/** Uit GET /api/pension/beschikbaarheid. */
export interface Beschikbaarheid {
  beschikbaar: boolean;
  capaciteitIngesteld: boolean;
  nachten: number;
  dieren: number;
  knelpunten: { datum: string; diersoort: string | null; reden: "VOL" | "CAPACITEIT"; vrij: number | null }[];
}

export type Vertaal = ReturnType<typeof useTranslate>;

/** "Fam. M van Diessen" → "MD": de eerste letter van het eerste en het laatste woord, zonder aanhef. */
export function initialen(naam: string): string {
  const woorden = naam
    .replace(/^(fam|dhr|mevr|mw|mr)\.?\s+/i, "")
    .split(/\s+/)
    .filter((w) => /^\p{L}/u.test(w));
  if (woorden.length === 0) return "?";
  const laatste = woorden.length > 1 ? woorden[woorden.length - 1][0] : "";
  return `${woorden[0][0]}${laatste}`.toUpperCase();
}

export function datum(iso: string, locale: string): string {
  return datumMetJaar(iso, locale).replace(/^0/, "");
}

/** "11 april 2027". */
export function langeDatum(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

export function isDatum(iso: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) && !Number.isNaN(new Date(`${iso}T00:00:00Z`).getTime());
}

/** Of vertrek na aankomst ligt; op één dag moet de vertrektijd later zijn. */
export function geldigePeriode(p: Periode): boolean {
  if (!isDatum(p.aankomst) || !isDatum(p.vertrek)) return false;
  return p.vertrek > p.aankomst || (p.vertrek === p.aankomst && p.vertrekTijd > p.aankomstTijd);
}

export function beschikbaarheidUrl(p: Periode, dierIds: number[]): string {
  const params = new URLSearchParams({ van: p.aankomst, tot: p.vertrek });
  dierIds.forEach((id) => params.append("dierIds", String(id)));
  return `pension/beschikbaarheid?${params}`;
}

/** De dieren van de klant; Refine deelt de aanroep tussen de stappen die hem gebruiken. */
export function useKlantDieren(klantId: number) {
  const res = useCustom<PensionKlantData>({ url: `pension/klanten/${klantId}`, method: "get" });
  const data = res.data?.data;
  return {
    ...res,
    dieren: Array.isArray(data?.dieren) ? data!.dieren : [],
    reserveringen: Array.isArray(data?.reserveringen) ? data!.reserveringen : [],
  };
}

export function Voet({ links, rechts }: { links?: ReactNode; rechts?: ReactNode }) {
  return (
    <footer className="nr__voet">
      <div className="nr__voet-deel">{links}</div>
      <div className="nr__voet-deel">{rechts}</div>
    </footer>
  );
}

export function Terug({ t, onClick, disabled, label }: { t: Vertaal; onClick?: () => void; disabled?: boolean; label?: string }) {
  return (
    <button type="button" className="nr-knop nr-knop--rand" onClick={onClick} disabled={disabled}>
      <ChevronLeft size={17} aria-hidden="true" />
      {label ?? t("nieuweReservering.terug")}
    </button>
  );
}

export function Volgende({
  t,
  onClick,
  disabled,
  label,
}: {
  t: Vertaal;
  onClick?: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button type="button" className="nr-knop nr-knop--hoofd" onClick={onClick} disabled={disabled}>
      {label ?? t("nieuweReservering.volgende")}
      <ChevronRight size={17} aria-hidden="true" />
    </button>
  );
}

export function Veld({
  label,
  verplicht,
  className,
  children,
}: {
  label: string;
  verplicht?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={className ? `nr-veld ${className}` : "nr-veld"}>
      <span className="nr-veld__label">
        {label}
        {verplicht && (
          <span className="nr-veld__ster" aria-hidden="true">
            {" *"}
          </span>
        )}
      </span>
      {children}
    </label>
  );
}

export function Schakel({ label, aan, onChange }: { label: string; aan: boolean; onChange: (aan: boolean) => void }) {
  return (
    <label className="nr-schakel">
      <input type="checkbox" role="switch" checked={aan} onChange={(e) => onChange(e.target.checked)} />
      <span className="nr-schakel__baan" aria-hidden="true" />
      {label}
    </label>
  );
}

/** De gekozen klant bovenaan een stap, met een knop om een andere te kiezen of hem te bekijken. */
export function KlantBalk({
  klant,
  label,
  knop,
  knopIcoon,
  onKnop,
}: {
  klant: GekozenKlant;
  label?: string;
  knop: string;
  knopIcoon: ReactNode;
  onKnop: () => void;
}) {
  return (
    <section className="nr-gekozen" aria-label={klant.naam}>
      <span className="nr-klant__avatar" aria-hidden="true">
        {initialen(klant.naam)}
      </span>
      <div className="nr-gekozen__naam">
        {label && <span>{label}</span>}
        <strong>{klant.naam}</strong>
      </div>
      <div className="nr-gekozen__contact">
        {klant.adres && (
          <span className="nr-regel">
            <Home size={16} aria-hidden="true" />
            {klant.adres}
          </span>
        )}
        {(klant.email || klant.telefoon) && (
          <span className="nr-regel">
            {klant.email && (
              <>
                <Mail size={16} aria-hidden="true" />
                {klant.email}
              </>
            )}
            {klant.email && klant.telefoon && <span className="nr-punt">•</span>}
            {klant.telefoon && (
              <>
                <Phone size={16} aria-hidden="true" />
                {klant.telefoon}
              </>
            )}
          </span>
        )}
      </div>
      <button type="button" className="nr-knop nr-knop--rand" onClick={onKnop}>
        {knopIcoon}
        {knop}
      </button>
    </section>
  );
}
